import React, { useCallback, useMemo, useContext, useState, useEffect } from 'react';
import { IStyle } from 'Styles';
import './style.scss';
import { handleTableFilter } from './baseGroup';
import { GroupDTO, NotificationTypeDTO, UserDTO } from 'business/models/Models';
import { UserGroupContext } from '../../Licensing/userSetup';
import { RMModal } from 'components/modal';
import { getNewId, getUserId, isRMWebLicensed, showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { EUserTypes } from 'enums/EFeatureTypes';
import { NotificationTypes } from 'constants/types';
import RMTable from 'components/customComponents/Controls/RMTable';
import HeaderBar from 'components/main/Setup/CrystalContainer/HeaderBar';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import { actionButtonStyle } from 'components/main/Setup/CrystalContainer/Constants';
import GenerateOTPByAdminDialogue from './GenerateOTPByAdminDialogue';
import { ALL_ROCKMAKER_USERS } from '../constants';

interface Props {
  onSelectedUserChange: (event: any) => void;
  selectedUsers: UserDTO[];
  hideActionContainer?: boolean;
  columnsToShow?: string[];
  headerText?: string;
  tableContainerStyle?: React.CSSProperties;
  setSelectedUsers?: (users: UserDTO[]) => void;
  showUnsavedUsers?: boolean;
  userIdsToHide?: number[];
  setEditId: React.Dispatch<React.SetStateAction<number>>;
  editId: number;
}

interface ITableColumn {
  accessor?: any;
  Filter?: any;
  Header?: any;
  className?: any;
  Cell?: (arg: any) => any;
}

const deleteProtectedUserNames = ['Administrator', 'Default User', 'FormulatrixAdmin'];

const initialModalState = {
  heading: '',
  content: null,
  okText: '',
  isConfirmationModal: false,
  onOkPress: null,
};

export const copyOTPToClipboard = async (otp?: number) => {
  if (!otp) {
    showAlertSnackBar(
      EAlertType.ERROR,
      ESnackBarPosition.TOP,
      'Failed to generate OTP number',
    );
    return;
  }

  try {
    if (!navigator.clipboard?.writeText) {
      throw new Error('Clipboard unavailable');
    }

    await navigator.clipboard.writeText(otp.toString());
    showAlertSnackBar(
      EAlertType.SUCCESS,
      ESnackBarPosition.TOP,
      'OTP has been copied successfully.',
    );
  } catch {
    showAlertSnackBar(
      EAlertType.ERROR,
      ESnackBarPosition.TOP,
      'Failed to copy OTP to clipboard',
    );
  }
};

const User = (props: Props) => {
  const {
    onSelectedUserChange,
    selectedUsers,
    columnsToShow = null,
    headerText = 'Users',
    tableContainerStyle,
    setSelectedUsers = null,
    showUnsavedUsers = true,
    userIdsToHide = [],
    editId,
    setEditId,
  } = props;
  const { tempState, setTempState, rowError, setIsDirty, setRowError } =
    useContext(UserGroupContext);
  const [showModal, setShowModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [currentModal, setCurrentModal] = useState(initialModalState);
  const selectedUserIds = useMemo(() => selectedUsers.map((u) => u.id), [selectedUsers]);
  const [userId, setUserId] = useState(0);

  useEffect(() => {
    getUserId().then((id) => {
      setUserId(parseInt(id));
    });
  }, []);

  useEffect(() => {
    if (selectedUserIds.length) {
      const indices = [];
      const filteredUserList = tempState.users.filter(
        (u) => !userIdsToHide.includes(u.id),
      );
      filteredUserList.forEach((user, index) => {
        if (selectedUserIds.includes(user.id)) {
          indices.push(index);
        }
      });
      if (indices.length) {
        setSelectedRows(indices);
      }
    }
  }, [selectedUserIds, tempState]);

  const getTableColumns: ITableColumn[] = useMemo(() => {
    const rmwLicensed = isRMWebLicensed();
    const accessSelection =
      rmwLicensed && tempState?.userTypes
        ? tempState.userTypes
            ?.filter((t) => t.id !== EUserTypes.All)
            ?.map((userType) => ({
              id: `user-tablecolumns-accessselection-usertype-${userType.id}`,
              Header: userType.name,
              accessor: `userType_${userType.id}`,
              className: 'w-2 flex align-center justify-center',
              Cell: ({ cell }: any) => {
                const { original } = cell.row;
                return (
                  <input
                    id={`user-tablecolumns-accessselection-usertype-${original.userTypeId}-input`}
                    type="checkbox"
                    checked={
                      original.userTypeId === userType.id ||
                      original.userTypeId === EUserTypes.All
                    }
                    className="rounded-sm w-6 h-6"
                    onChange={(e) =>
                      onCellChange(e, original, String(userType.id), 'checkbox')
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  />
                );
              },
            }))
        : [];

    return [
      {
        id: 'user-tablecolumns-name',
        accessor: 'name',
        Filter: handleTableFilter('Name'),
        Cell: ({ cell }: any) =>
          editId !== cell.row.original.id ? (
            cell.row.original.name
          ) : (
            <input
              id={`user-tablecolumns-name-${cell.row.original.name}-input`}
              type="text"
              className="rounded-sm bg-white p-1 w-full"
              onChange={(e) => onCellChange(e, cell.row.original, 'name', 'text')}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              value={cell.row.original.name}
            />
          ),
      },
      {
        id: 'user-tablecolumns-email',
        accessor: 'email',
        Filter: handleTableFilter('Email'),
        Cell: ({ cell }: any) =>
          editId !== cell.row.original.id ? (
            cell.row.original.email
          ) : (
            <input
              id={`user-tablecolumns-email-${cell.row.original.email}-input`}
              type="text"
              className="rounded-sm bg-white p-1 w-full"
              onChange={(e) => onCellChange(e, cell.row.original, 'email', 'text')}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              value={cell.row.original.email}
            />
          ),
      },
      ...accessSelection,
      {
        id: 'user-tablecolumns-notificationtypename',
        accessor: 'notificationTypeName',
        Filter: handleTableFilter('Notification Type'),
        Cell: ({ cell }: any) =>
          editId !== cell.row.original.id ? (
            cell.row.original.notificationType?.name
          ) : (
            <select
              id={`user-tablecolumns-notificationtypename-${cell.row.original.notificationType?.name}-select`}
              className="rounded-sm bg-white p-1 w-full"
              style={{ width: '80%', padding: '0px', margin: '0px' }}
              value={cell.row.original.notificationType?.name}
              onChange={(e) =>
                onCellChange(e, cell.row.original, 'notificationTypeName', 'select')
              }
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              disabled={false}
            >
              {NotificationTypes.map((val) => (
                <option
                  id={`notificationtypename-option-${val.id}`}
                  key={val.id}
                  value={val.name}
                >
                  {val.name}
                </option>
              ))}
            </select>
          ),
      },
      {
        accessor: 'action',
        Header: 'Action',
        className: '!justify-center max-w-[10%]',
      },
    ];
  }, [editId, tempState?.userTypes]);

  const _tableColumns: ITableColumn[] = useMemo(
    () =>
      columnsToShow
        ? getTableColumns.filter((column) => columnsToShow.includes(column.accessor))
        : getTableColumns,
    [columnsToShow, getTableColumns],
  );

  const shouldAllowUserDelete = useCallback(() => {
    if (selectedUserIds.includes(userId)) {
      return [false, 'You cannot delete the current user.'];
    }

    if (selectedUsers.some((user) => deleteProtectedUserNames.includes(user.name))) {
      return [false, 'You cannot delete the Administrator and Default User'];
    }

    return [true, ''];
  }, [selectedUserIds, selectedUsers, userId]);

  const actionButtons = useCallback(
    (user: UserDTO) => {
      return (
        <div
          id="user-actionbuttons-wrapper"
          className="flex flex-row justify-between w-2/3"
        >
          <RMCustomButton
            onClick={() => onGenerateOTPThroughAdmin(user)}
            titleTip={'Reset Password'}
            iconProps={{
              name: 'ResetPassword',
              fill: 'white',
              width: '2rem',
              height: '2rem',
            }}
            contentContainerStyle={{
              ...actionButtonStyle,
              backgroundColor: '#006AF5',
            }}
            isDisabled={user.id < 0}
          />
          <RMCustomButton
            onClick={() => onViewOTP(user)}
            titleTip={'View Code'}
            iconProps={{ name: 'ViewOTP', fill: 'white', width: '2rem', height: '2rem' }}
            contentContainerStyle={{
              ...actionButtonStyle,
              backgroundColor: '#006AF5',
            }}
            isDisabled={user.id < 0}
          />
        </div>
      );
    },
    [selectedRows, tempState],
  );

  const onGenerateOTPThroughAdmin = (user) => {
    setCurrentModal((prv) => ({
      ...prv,
      heading: 'Reset Password',
      okText: 'Generate CODE',
      content: <GenerateOTPByAdminDialogue onViewOTP={onViewOTP} user={user} />,
      isConfirmationModal: false,
    }));
    setShowModal(true);
  };

  const onViewOTP = (user: UserDTO) => {
    setCurrentModal((prv) => ({
      ...prv,
      heading: 'View Code',
      okText: 'COPY CODE',
      content: viewOTPContent(user),
      isConfirmationModal: true,
      onOkPress: () => onCopyOTP(user.userOTP),
    }));
    setShowModal(true);
  };

  const viewOTPContent = (user: UserDTO) => {
    return (
      <div id={`user-viewotpcontent-${user.name}`} className="flex flex-column">
        <p id={`user-viewotpcontent-${user.name}-title`}>Verify your data below</p>
        <div id={`user-viewotpcontent-${user.name}-username`} className="flex flex-row">
          <p id={`user-viewotpcontent-${user.name}-username-title`} className="w-1/3">
            Username
          </p>
          <p id={`user-viewotpcontent-${user.name}-username-text`}>{user.name}</p>
        </div>
        <div id={`user-viewotpcontent-${user.name}-email`} className="flex flex-row">
          <p id={`user-viewotpcontent-${user.name}-email-title`} className="w-1/3">
            Email
          </p>
          <p id={`user-viewotpcontent-${user.name}-email-text`}>{user.email}</p>
        </div>
        <div
          id={`user-viewotpcontent-${user.name}-generatecode`}
          className="flex flex-row"
        >
          <p id={`user-viewotpcontent-${user.name}-generatecode-title`} className="w-1/3">
            Generated Code
          </p>
          <p id={`user-viewotpcontent-${user.name}-generatecode-text`}>
            {user.userOTP ? user.userOTP : '-'}
          </p>
        </div>
        <div
          id={`user-viewotpcontent-${user.name}-expirationdate`}
          className="flex flex-row"
        >
          <p
            id={`user-viewotpcontent-${user.name}-expirationdate-title`}
            className="w-1/3"
          >
            Expiration Date
          </p>
          <p id={`user-viewotpcontent-${user.name}-expirationdate-text`}>
            {user.userOTPValidity}
          </p>
        </div>
      </div>
    );
  };

  const onCopyOTP = (otp?: number) => {
    copyOTPToClipboard(otp);
  };

  const userData = useMemo(
    () =>
      tempState.users
        ?.map((user) => {
          if ((!showUnsavedUsers && user.id < 1) || userIdsToHide.includes(user.id)) {
            return null;
          }
          return {
            id: user.id,
            name: user.name,
            email: user.email,
            notificationType: user.notificationType,
            notificationTypeName: user.notificationType?.name,
            action: actionButtons(user),
            groups: user.groups,
            userTypeId: user.userType.id,
          };
        })
        .filter((u) => u) || [],
    [tempState, selectedRows, userIdsToHide, editId],
  );

  const handleAdd = useCallback(() => {
    const newUserId = getNewId();

    const deafultNotificationType: NotificationTypeDTO = new NotificationTypeDTO({
      id: NotificationTypes.filter((type) => type.id == 2)[0]?.id,
      name: NotificationTypes.filter((type) => type.id == 2)[0]?.name,
    });

    const defaultGroup: GroupDTO = new GroupDTO({
      id: tempState?.groups?.find((g) => g.name === ALL_ROCKMAKER_USERS).id,
      name: ALL_ROCKMAKER_USERS,
      isPrimaryGroup: true,
    });

    const newUser: UserDTO = UserDTO.fromJS({
      id: newUserId,
      name: '',
      email: '',
      userType: tempState.userTypes.find((usrTyp) => usrTyp.id === 2),
      notificationType: deafultNotificationType,
      groups: [defaultGroup],
      isGroupAdmin: false,
    });

    const usersList = [...tempState.users];
    usersList.push(newUser);

    const groups = [...tempState.groups];
    groups.find((g) => g.name === ALL_ROCKMAKER_USERS)?.users?.push(newUser);

    setEditId(newUserId);
    setSelectedRows([0]);
    setSelectedUsers([newUser]);
    setTempState({ ...tempState, users: usersList.sort((a, b) => a.id - b.id), groups });
    setIsDirty(true);
  }, [setCurrentModal, setShowModal, tempState, selectedRows]);

  const handleEdit = useCallback(() => {
    setEditId(selectedUserIds[0]);
  }, [selectedUsers, tempState, selectedRows]);

  const handleDeleteUser = useCallback(() => {
    const [allowUserDelete, userDeleteMessage] = shouldAllowUserDelete();
    if (!allowUserDelete) {
      setCurrentModal(initialModalState);
      setShowModal(false);
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        userDeleteMessage as string,
      );
      return;
    }

    const users: UserDTO[] = tempState.users.filter(
      (user) => !selectedUserIds.includes(user.id),
    );
    const groups: GroupDTO[] = tempState.groups ?? [];
    groups.forEach((group) => {
      group.users = group.users?.filter((user) => !selectedUserIds.includes(user.id));
    });
    if (setSelectedUsers) {
      setSelectedUsers([]);
    }

    setSelectedRows([]);
    setRowError((prv) => ({ ...prv, users: {} }));
    setTempState((prv) => ({ ...prv, users, groups }));
    setCurrentModal(initialModalState);
    setShowModal(false);
  }, [selectedUserIds]);

  const handleDeleteConfirmation = useCallback(() => {
    setCurrentModal((prv) => ({
      ...prv,
      heading: 'Delete User',
      okText: 'Delete',
      content: 'Delete this user?',
      isConfirmationModal: true,
      onOkPress: handleDeleteUser,
    }));
    setShowModal(true);
  }, [selectedUserIds]);

  const handleSelectionChange = useCallback(
    (rowIndices) => {
      setSelectedRows(rowIndices);
      onSelectedUserChange(rowIndices);
      setEditId(0);
    },
    [onSelectedUserChange],
  );

  const onCopyEmailClick = useCallback(() => {
    const emails: string[] = tempState.users.map((user: UserDTO) => user.email);
    navigator.clipboard.writeText(emails.toString());
    showAlertSnackBar(
      EAlertType.SUCCESS,
      ESnackBarPosition.TOP,
      'Emails have been copied successfully.',
    );
  }, [tempState]);

  const onCellChange = (
    e: React.ChangeEvent<any>,
    value: UserDTO,
    key: string,
    type: string,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    if (editId !== value.id) {
      return;
    }

    const tempUsers = [...tempState.users];

    if (type === 'text') {
      for (const usr of tempUsers) {
        if (usr.id === value.id) {
          usr[key] = e.target.value;
          break;
        }
      }
    } else if (type === 'select') {
      for (const usr of tempUsers) {
        if (usr.id === value.id) {
          usr['notificationType'] = NotificationTypes[e.target.selectedIndex];
          break;
        }
      }
    } else if (type === 'checkbox') {
      for (const usr of tempUsers) {
        if (usr.id === value.id) {
          if (e.target.checked) {
            usr[`userType_${key}`] = e.target.checked;
            usr['userType'] = [...tempState.userTypes].find((type) =>
              type.name.includes('/'),
            );
          } else if (!e.target.checked && usr.userType.name.includes('/')) {
            usr[`userType_${key}`] = e.target.checked;
            usr['userType'] = [...tempState.userTypes].find(
              (type) => !type.name.includes('/') && type.id != key,
            );
          } else if (!e.target.checked && !usr.userType.name.includes('/')) {
            usr[`userType_${key}`] = !e.target.checked;
          }
          break;
        }
      }
    }
    setTempState({ ...tempState, users: tempUsers });
  };

  const handleOnRowCLick = (row) => {
    setSelectedRows([row.index]);
    onSelectedUserChange([row.index]);
    setEditId(0);
  };

  return (
    <>
      {headerText === 'Users' && (
        <HeaderBar
          title={headerText}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDeleteConfirmation}
          onCopyEmail={onCopyEmailClick}
          enableEditDelete={selectedUsers.length > 0}
          disableEdit={selectedUsers.length !== 1}
        />
      )}
      <div
        id="sections-user-table-container"
        className="user-setup-table table-main-container h-[85%]"
        style={{ ...styles.tableContainer, ...tableContainerStyle }}
      >
        <RMTable
          columns={_tableColumns}
          data={userData}
          onCheckboxSelectionChange={handleSelectionChange}
          selectedIds={selectedRows}
          rowExtraInfo={rowError['users']}
          onRowClicked={handleOnRowCLick}
        />
      </div>
      <RMModal
        id="sections-user-modal"
        setShowModal={setShowModal}
        showModal={showModal}
        body={currentModal.content}
        heading={currentModal.heading}
        showButtons={currentModal.isConfirmationModal}
        onOk={currentModal.onOkPress}
        okButtonText={currentModal.okText}
        noButtonText={'Close'}
      />
    </>
  );
};

const styles: IStyle = {
  headerContainer: {
    padding: 20,
    paddingTop: 8,
  },
  headerTitle: {
    fontWeight: 'bold',
  },
  resetPasswordBtn: {
    width: '80%',
    backgroundColor: '#3F9243',
    fontSize: '10px',
  },
  resetPasswordBtnDisabled: {
    backgroundColor: '#E0E0E0',
    color: '#CACACA',
  },
};

export default User;
